/**
 * App shell + auth gate + capability-gated navigation.
 *
 * Flow: capture any SSO-handoff token from the URL, then read /account/context.
 * No/invalid session → the sign-in shell. With a session, the nav shows only the
 * tabs the caller's capabilities unlock (assign_role → Users, manage_trash /
 * force_release_lock → Skills + Trash, manage_billing → Billing). A 401 anywhere
 * clears the session and drops back to sign-in.
 */

import { useEffect, useMemo, useState } from "react";
import { api } from "./api/client";
import { setUnauthorizedHandler } from "./api/client";
import type { AccountContext, Capability } from "./api/types";
import { captureSessionFromUrl, clearToken, getToken } from "./auth/session";
import { RoleChip } from "./components/Chips";
import { ToastProvider } from "./components/Toast";
import { SignIn } from "./screens/SignIn";
import { UsersRoles } from "./screens/UsersRoles";
import { Skills } from "./screens/Skills";
import { Trash } from "./screens/Trash";
import { Billing } from "./screens/Billing";
import { Handoffs } from "./screens/Handoffs";
import { Download } from "./screens/Download";

type TabId = "download" | "users" | "skills" | "trash" | "handoffs" | "billing";
interface Tab {
  id: TabId;
  label: string;
  /** Any-of capabilities that unlock the tab. Empty = always available to any signed-in user. */
  requires: Capability[];
  /** Extra gate beyond capabilities (e.g. a server-side allow-list flag). Default: allowed. */
  extraGate?: (ctx: AccountContext) => boolean;
}
const TABS: Tab[] = [
  // Download is shown to everyone — an onboarded tester (TRE-1044/1047) is often a
  // plain runner with no admin capability, and the installer is the first thing
  // they need. The server still entitlement-gates the actual bytes (TRE-1713).
  { id: "download", label: "Download", requires: [] },
  { id: "users", label: "Users & Roles", requires: ["assign_role"] },
  { id: "skills", label: "Skills", requires: ["force_release_lock", "manage_trash"] },
  { id: "trash", label: "Trash", requires: ["manage_trash"] },
  // Dev-handoff viewer (TRE-987): the beta feedback-loop read surface. This is a
  // CROSS-TENANT Treillage-maintainer view, NOT per-account data — so it is gated on
  // the server's `handoffMaintainer` allow-list flag, not merely manage_trash. A client
  // firm's own admin/owner never sees this tab (and the API 403s them regardless).
  { id: "handoffs", label: "Dev Handoffs", requires: ["manage_trash"], extraGate: (ctx) => !!ctx.handoffMaintainer },
  { id: "billing", label: "Billing", requires: ["manage_billing"] },
];

export function App() {
  return (
    <ToastProvider>
      <Root />
    </ToastProvider>
  );
}

function Root() {
  const [captured] = useState(() => captureSessionFromUrl());
  const [ctx, setCtx] = useState<AccountContext | null>(null);
  const [phase, setPhase] = useState<"loading" | "signed-out" | "ready" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const loadContext = useMemo(
    () => async () => {
      if (!getToken()) {
        setPhase("signed-out");
        return;
      }
      setPhase("loading");
      try {
        const c = await api.accountContext();
        setCtx(c);
        setPhase("ready");
      } catch (e) {
        // A 401 already cleared the token + fired the unauthorized handler.
        if (!getToken()) {
          setPhase("signed-out");
        } else {
          setErrorMsg((e as Error).message);
          setPhase("error");
        }
      }
    },
    [],
  );

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setCtx(null);
      setPhase("signed-out");
    });
  }, []);

  useEffect(() => {
    void loadContext();
    // `captured` is only read to force the initial load after a URL token grab.
  }, [loadContext, captured]);

  if (phase === "loading") {
    return <div className="center-pane"><p className="muted">Loading…</p></div>;
  }
  if (phase === "signed-out") {
    return <SignIn onSignedIn={() => void loadContext()} />;
  }
  if (phase === "error" || !ctx) {
    return (
      <div className="center-pane">
        <div className="card">
          <span className="brand">Treillage Admin</span>
          <div className="banner banner--error" role="alert">
            {errorMsg ?? "Couldn't load your account."}
          </div>
          <button className="btn" onClick={() => void loadContext()}>Retry</button>
        </div>
      </div>
    );
  }

  return <Shell ctx={ctx} onSignOut={() => { clearToken(); setCtx(null); setPhase("signed-out"); }} />;
}

function Shell({ ctx, onSignOut }: { ctx: AccountContext; onSignOut: () => void }) {
  const available = TABS.filter(
    (t) =>
      (t.requires.length === 0 || t.requires.some((c) => ctx.capabilities.includes(c))) &&
      (t.extraGate?.(ctx) ?? true),
  );
  const [tab, setTab] = useState<TabId>(available[0]?.id ?? "download");
  const active = available.some((t) => t.id === tab) ? tab : available[0]?.id;

  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">Treillage Admin</span>
        <nav className="nav" aria-label="Sections">
          {available.map((t) => (
            <button
              key={t.id}
              aria-current={active === t.id ? "page" : undefined}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <div className="identity-chip">
          <span>{ctx.identity.email}</span>
          <RoleChip role={ctx.identity.role} />
          <button className="btn btn--small" onClick={onSignOut}>Sign out</button>
        </div>
      </header>
      <main className="content">
        {active === "download" && <Download ctx={ctx} />}
        {active === "users" && <UsersRoles ctx={ctx} />}
        {active === "skills" && <Skills ctx={ctx} />}
        {active === "trash" && <Trash ctx={ctx} />}
        {active === "handoffs" && <Handoffs ctx={ctx} />}
        {active === "billing" && <Billing ctx={ctx} />}
      </main>
    </div>
  );
}
